import { db } from '@/data/firebase/config';
import { useAuth } from '@/domain/context/auth';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { AdminLog, AdminLogCreate } from '@/domain/types/admin-log';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

export const useAdminLogger = () => {
  const { user } = useAuth();

  const logAction = async (logData: AdminLogCreate): Promise<void> => {
    if (!user) {
      console.warn('No authenticated user for logging action');
      return;
    }

    try {
      const logEntry: Omit<AdminLog, 'id'> = {
        ...logData,
        adminId: user.id,
        adminName: `${user.name} ${user.lastname}`,
        adminEmail: user.email,
        timestamp: logData.timestamp || new Date(),
      };

      await addDoc(collection(db, DOCUMENTS_DB.ADMIN_LOGS), {
        ...logEntry,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error logging admin action:', error);
    }
  };

  const logCreate = async (
    collection: string,
    documentId: string,
    documentTitle?: string,
    changes?: Record<string, any>,
  ): Promise<void> => {
    await logAction({
      action: 'CREATE',
      collection,
      documentId,
      documentTitle,
      changes,
    });
  };

  const logUpdate = async (
    collection: string,
    documentId: string,
    documentTitle?: string,
    changes?: Record<string, any>,
  ): Promise<void> => {
    await logAction({
      action: 'UPDATE',
      collection,
      documentId,
      documentTitle,
      changes,
    });
  };

  const logDelete = async (
    collection: string,
    documentId: string,
    documentTitle?: string,
  ): Promise<void> => {
    await logAction({
      action: 'DELETE',
      collection,
      documentId,
      documentTitle,
    });
  };

  const logReleaseToAll = async (
    collection: string,
    documentId: string,
    documentTitle?: string,
    previousAudienceIds?: string[],
  ): Promise<void> => {
    await logAction({
      action: 'RELEASE_TO_ALL',
      collection,
      documentId,
      documentTitle,
      changes: { previousAudienceIds },
    });
  };

  return {
    logCreate,
    logUpdate,
    logDelete,
    logReleaseToAll,
    logAction,
  };
};
